import React, { useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Form, Input, Button, Alert, Card, Spin, Row, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import AuthServices from "../../../services/AuthServices";

import "simple-react-validator/dist/locale/es";
import { translate, get_language } from "../../../locale";

const ChangePassword = () => {
  const { username, code } = useParams();
  const [values, setValues] = useState({
    current_password: "",
    password: "",
    confirm: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [, forceUpdate] = useState();
  const navigate = useNavigate();

  const simpleValidator = useRef(
    new SimpleReactValidator({
      locale: get_language,
      autoForceUpdate: { forceUpdate: forceUpdate },
      element: (message) => (
        <div className="text-red-600 pt-2 text-xs">{message}</div>
      ),
      messages: {
        password:
          "La contraseña debe contener al menos una mayúscula, una minúscula, un número y un mínimo de 8 caracteres",
        sameValue: "Las contraseñas no coinciden",
      },
      validators: {
        sameValue: {
          message: translate("Las contraseñas no coinciden"),
          rule: (val, params, validator) => val === params[0],
          required: true,
        },
        password: {
          rule: (val, params) => {
            const uppercaseRegExp = /(?=.*?[A-Z])/;
            const lowercaseRegExp = /(?=.*?[a-z])/;
            const digitsRegExp = /(?=.*?[0-9])/;
            const minLengthRegExp = /.{8,}/;

            return (
              val.length >= 8 &&
              uppercaseRegExp.test(val) === true &&
              lowercaseRegExp.test(val) === true &&
              digitsRegExp.test(val) === true &&
              minLengthRegExp.test(val) === true
            );
          },
        },
      },
    })
  );

  const disabled = () => !simpleValidator.current.allValid();

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate();
      setError("El usuario y/o contraseña son incorrectos.");
      return;
    }
    try {
      const data = {
        password: values.current_password,
        new_password: values.password,
      };
      const res = await AuthServices.changePassword(data);

      if (
        res &&
        res.status !== undefined &&
        res.status >= 200 &&
        res.status < 300 &&
        res.data
      ) {
        setLoading(false);
        setValues({
          current_password: "",
          password: "",
          confirm: "",
        });
        simpleValidator.current.hideMessages();
        simpleValidator.current.visibleFields = [];
        forceUpdate();

        message.success(
          translate(
            "La {0} se actualizó exitosamente.",
            translate("contraseña")
          )
        );
        // navigate("/auth/login");
      } else if (res.data.error === true && res.data.message !== "") {
        setError(translate(res.data.message));
      } else {
        setError(
          translate("La {0} no pudo ser actualizada.", translate("contraseña"))
        );
      }
      setLoading(false);
    } catch (e) {
      console.log(e.message);
      if (e.message !== "") {
        setError(e.message);
      }
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  return (
    <div className="flex flex-row w-screen justify-center items-stretch min-h-full max-w-7xl py-24">
      <div className="w-1/2 flex justify-center">
        <Card
          title={translate("Cambiar contraseña")}
          className="w-7/12"
          bordered={false}
          headStyle={{ border: "none" }}
        >
          {error && (
            <Alert
              message={error}
              type="error"
              className="mb-6"
              closable
              onClose={() => setError("")}
            />
          )}

          <Form
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            className="w-full"
          >
            <Form.Item>
              <Input
                placeholder={translate("Contraseña actual")}
                className="border-custom_primary"
                name="current_password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.current_password}
              />
              {simpleValidator.current.message(
                "current_password",
                values.current_password,
                "required",
                get_language === "es"
                  ? {
                      messages: {
                        required: "El campo Contraseña actual es requerido",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            <Form.Item>
              <Input.Password
                placeholder={translate("Contraseña")}
                className="border-custom_primary"
                name="password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
              />
              {simpleValidator.current.message(
                "password",
                values.password,
                "required|password",
                get_language === "es"
                  ? {
                      messages: {
                        required: "El campo Contraseña es requerido",
                        password:
                          "La contraseña debe contener al menos una mayúscula, una minúscula, un número y un caracter especial (#?!@$%^&*-) y un mínimo de 8 caracteres",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            <Form.Item>
              <Input.Password
                placeholder={translate("Confirmar Contraseña")}
                className="border-custom_primary"
                name="confirm"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.confirm}
              />
              {simpleValidator.current.message(
                "confirm",
                values.confirm,
                `required|password|sameValue:${values.password}`,
                get_language === "es"
                  ? {
                      messages: {
                        required: "El campo Confirmar Contraseña es requerido",
                        password:
                          "La contraseña debe contener al menos una mayúscula, una minúscula, un número y un caracter especial (#?!@$%^&*-) y un mínimo de 8 caracteres",
                      },
                    }
                  : {}
              )}
            </Form.Item>
            <Row justify="end">
              <Button
                disabled={disabled() || loading}
                htmlType="submit"
                type="primary"
                className="bg-custom_primary"
              >
                {loading && (
                  <Spin
                    indicator={<LoadingOutlined style={{ color: "#666666" }} />}
                    className="mr-2"
                  />
                )}
                {translate("Aceptar")}
              </Button>
            </Row>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
